/**
 * Cost Center API (Phase D/E)
 *
 * Reads the owned `cost_events` ledger via /v1/admin/cost/* — real per-model and
 * per-agent cost (LLM + image/tts/stt), replacing the legacy flat model-blind
 * reports dashboard. All endpoints are admin-only and accept an optional
 * YYYY-MM-DD window (default last 30 days) + optional region filter.
 */

import apiClient from './client';
import { DEFAULT_EXCLUDE_TEAM } from '@/lib/analytics/teamFilter';

// ============================================================
// Types (mirror api/routers/admin/cost.py response models)
// ============================================================

export interface ModelCost {
  model_id: string;
  currency: string | null;
  calls: number;
  input_tokens: number;
  output_tokens: number;
  cost: number | null; // native currency when the entity has exactly one currency, otherwise null
  native_costs: Record<string, number>; // currency -> native amount; preserves mixed-currency detail
  cost_cny: number; // normalized entity total in CNY — single source on the backend
}

export interface AgentCost {
  agent_type: string | null; // null = unattributed (pre-Phase-E rows / non-chat feeds)
  currency: string | null;
  calls: number;
  cost: number | null;
  native_costs: Record<string, number>;
  cost_cny: number;
}

export interface DailyCost {
  day: string; // YYYY-MM-DD (UTC)
  currency: string;
  calls: number;
  cost: number;
  cost_cny: number;
}

// currency -> total cost
export type CostTotals = Record<string, number>;

function windowParams(
  startDate?: string,
  endDate?: string,
  region?: string,
  excludeTeam = DEFAULT_EXCLUDE_TEAM
): string {
  const params = new URLSearchParams();
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  if (region) params.append('region', region);
  params.append('exclude_team', String(excludeTeam));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export const costCenterApi = {
  getByModel: async (
    startDate?: string,
    endDate?: string,
    region?: string,
    excludeTeam = DEFAULT_EXCLUDE_TEAM
  ): Promise<ModelCost[]> => {
    const response = await apiClient.get(`/cost/by-model${windowParams(startDate, endDate, region, excludeTeam)}`);
    return response.data;
  },

  getByAgent: async (
    startDate?: string,
    endDate?: string,
    region?: string,
    excludeTeam = DEFAULT_EXCLUDE_TEAM
  ): Promise<AgentCost[]> => {
    const response = await apiClient.get(`/cost/by-agent${windowParams(startDate, endDate, region, excludeTeam)}`);
    return response.data;
  },

  getByDay: async (
    startDate?: string,
    endDate?: string,
    region?: string,
    excludeTeam = DEFAULT_EXCLUDE_TEAM
  ): Promise<DailyCost[]> => {
    const response = await apiClient.get(`/cost/by-day${windowParams(startDate, endDate, region, excludeTeam)}`);
    return response.data;
  },

  getTotals: async (
    startDate?: string,
    endDate?: string,
    region?: string,
    excludeTeam = DEFAULT_EXCLUDE_TEAM
  ): Promise<CostTotals> => {
    const response = await apiClient.get(`/cost/totals${windowParams(startDate, endDate, region, excludeTeam)}`);
    return response.data;
  },
};
